import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getAppOriginLabel } from '../../services/appOriginService';
import ChartCard, { ChartLegend, ChartTooltip } from './ChartCard';
import {
  ANIMATION,
  MOBILE_MQ,
  axisProps,
  gridProps,
  seriesColor,
  toCount,
  useChartAnimation,
  useMediaQuery,
} from './chartTheme';

const ChartScroll = styled.div`
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const ChartPlot = styled.div`
  min-width: ${({ $minWidth }) => `${$minWidth}px`};
`;

const CHART_HEIGHT = 340;
const CHART_HEIGHT_MOBILE = 280;

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

function formatPeriod(period, pattern) {
  if (!period || typeof period !== 'string') return '';
  const safe = period.length >= 7 ? `${period.slice(0, 7)}-01` : `${period}-01`;
  try {
    return capitalize(format(parseISO(safe), pattern, { locale: ptBR })) || period;
  } catch {
    return period;
  }
}

const TotalTicketsByAppChart = ({
  content = [],
  seriesOrder = [],
  loading = false,
  error = false,
  onRetry = null,
  filters = null,
}) => {
  const isMobile = useMediaQuery(MOBILE_MQ);
  const animate = useChartAnimation();
  const [activeKey, setActiveKey] = useState(null);

  const { chartData, total } = useMemo(() => {
    const rows = Array.isArray(content) ? content : [];
    const byPeriod = new Map();
    let sum = 0;
    for (const row of rows) {
      const period = row?.period ?? '';
      const origin = row?.appOrigin != null ? String(row.appOrigin) : '';
      const c = toCount(row?.count);
      if (!period) continue;
      if (!byPeriod.has(period)) {
        byPeriod.set(period, {
          period,
          monthLabel: formatPeriod(period, 'LLL/yy'),
          fullLabel: formatPeriod(period, "LLLL 'de' yyyy"),
        });
      }
      if (origin) {
        byPeriod.get(period)[origin] = c;
        sum += c;
      }
    }

    return {
      chartData: [...byPeriod.values()].sort((a, b) =>
        String(a.period).localeCompare(String(b.period))
      ),
      total: sum,
    };
  }, [content]);

  const orderedOrigins = useMemo(() => {
    const order = Array.isArray(seriesOrder) ? seriesOrder : [];
    const seen = new Set();
    const out = [];
    for (const o of order) {
      const k = String(o);
      if (k && !seen.has(k)) {
        seen.add(k);
        out.push(k);
      }
    }
    for (const row of chartData) {
      Object.keys(row).forEach((key) => {
        if (key === 'period' || key === 'monthLabel' || key === 'fullLabel') return;
        if (!seen.has(key)) {
          seen.add(key);
          out.push(key);
        }
      });
    }
    return out;
  }, [seriesOrder, chartData]);

  const legendItems = orderedOrigins.map((origin, idx) => ({
    key: origin,
    label: getAppOriginLabel(origin),
    color: seriesColor(idx),
  }));

  const showDots = chartData.length <= 2;
  const minPlotWidth = isMobile ? Math.max(320, chartData.length * 52) : 0;

  return (
    <ChartCard
      title="Tickets por aplicação ao longo do tempo"
      description="Evolução mensal por origem do app"
      stat={total}
      statLabel="tickets no período"
      toolbar={filters}
      loading={loading}
      error={error}
      onRetry={onRetry}
      isEmpty={chartData.length === 0}
      skeleton="columns"
      skeletonHeight={isMobile ? CHART_HEIGHT_MOBILE : CHART_HEIGHT}
    >
      <ChartLegend
        items={legendItems}
        variant="line"
        activeKey={activeKey}
        onActiveChange={setActiveKey}
        label="Origens do app"
      />
      <ChartScroll>
        <ChartPlot $minWidth={minPlotWidth}>
          <ResponsiveContainer width="100%" height={isMobile ? CHART_HEIGHT_MOBILE : CHART_HEIGHT}>
            <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid {...gridProps} vertical={false} />
              <XAxis
                dataKey="monthLabel"
                {...axisProps}
                interval="preserveStartEnd"
                minTickGap={16}
                tickMargin={10}
              />
              <YAxis allowDecimals={false} width={40} {...axisProps} />
              <Tooltip
                cursor={{ stroke: 'var(--color-border-secondary)', strokeWidth: 1 }}
                content={
                  <ChartTooltip
                    nameFormatter={getAppOriginLabel}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.fullLabel ?? ''}
                    showTotal
                  />
                }
              />
              {orderedOrigins.map((origin, idx) => (
                <Line
                  key={origin}
                  type="monotone"
                  dataKey={origin}
                  name={origin}
                  stroke={seriesColor(idx)}
                  strokeWidth={activeKey === origin ? 2.5 : 2}
                  strokeOpacity={activeKey && activeKey !== origin ? 0.15 : 1}
                  dot={showDots ? { r: 3, strokeWidth: 0, fill: seriesColor(idx) } : false}
                  activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--color-bg-primary)' }}
                  connectNulls
                  isAnimationActive={animate}
                  {...ANIMATION}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartPlot>
      </ChartScroll>
    </ChartCard>
  );
};

TotalTicketsByAppChart.propTypes = {
  content: PropTypes.arrayOf(
    PropTypes.shape({
      period: PropTypes.string,
      appOrigin: PropTypes.string,
      count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ),
  seriesOrder: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  error: PropTypes.bool,
  onRetry: PropTypes.func,
  filters: PropTypes.node,
};

export default TotalTicketsByAppChart;
